/**
 * S7 — the `case.source_catalog` reach path, which is what the `categorias`
 * lens groups on.
 *
 * Mirrors `useCaseFolderMap` / `useCaseWorkspaceTree` exactly (gateway-connected
 * gate, React Query, scope-distinct key) so the three payloads the Acervo joins
 * cache and invalidate the same way.
 *
 * ⚠ **This is the ONLY carrier of `categoryMeta`.** The marker rides the catalog
 * envelope, not `case.open`'s `capabilities` and not the folder map — verified
 * against the rolled runtime bundle rather than assumed. So the axis cannot be
 * offered until this query has answered, and a case whose catalog has not been
 * built yet correctly offers no axis at all.
 *
 * ⚠ **Known staleness (documented at the RPC):** the upload m-ingest mission
 * does not rebuild `_index.json` today — new sources appear only after the
 * m-ingest-verify reconciler runs. So a freshly uploaded file can be in the tree
 * and absent from the catalog for a while. That is exactly why
 * `buildCategoryFileGroups` keeps an unknown file in UNCATEGORIZED instead of
 * dropping it: the lens must degrade to "not classified yet", never to "your
 * document is not here".
 */

import { useQuery } from "@tanstack/react-query";
import { useGatewayStore } from "@/stores/useGatewayStore";
import {
  useActiveContextStore,
  useEffectiveScope,
} from "@/stores/useActiveContextStore";
import {
  scopeKeySegment,
  workspaceQueryEnabled,
} from "@/lib/workspace-scope";
import type { CaseSourceCatalogResult } from "@/types/gateway";

export function useCaseSourceCatalog() {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const selection = useActiveContextStore((s) => s.selection);
  const scope = useEffectiveScope();

  const clientId = selection?.clientId ?? undefined;
  const matterId = selection?.matterId ?? undefined;
  const caseId = selection?.caseId ?? undefined;

  return useQuery<CaseSourceCatalogResult, Error>({
    queryKey: ["case-source-catalog", scopeKeySegment(scope, caseId)],
    queryFn: () =>
      rpc!.caseSourceCatalog({
        clientId: clientId!,
        matterId: matterId!,
        caseId: caseId!,
      }),
    // No org branch: the catalog is a CASE artifact (`sources/_index.json`).
    // The org workspace is case-SHAPED but has no ingest pipeline, so asking
    // for one there would be a question the server cannot answer.
    // ⚠ Requires the FULL parent chain, unlike every sibling workspace query.
    // `case.source_catalog`'s params are `{clientId, matterId, caseId}` all
    // REQUIRED (types/gateway.ts), while `workspaceQueryEnabled` needs only a
    // caseId because D-8 flat cases open by caseId alone.
    //
    // ⇒ **A flat / assignment-pending case gets NO category axis.** That is a
    // real limitation and it is gated here rather than papered over with a
    // cast: sending `undefined` parents to an RPC that requires them would
    // trade a missing axis for an error, and the axis is optional by design —
    // absent `categoryMeta` already means "no axis", which degrades correctly.
    // Reported to `case-storage`; the fix, if wanted, is relaxing the RPC to
    // caseId-alone the way `document.list` already resolves.
    enabled:
      Boolean(rpc) &&
      connectionState === "connected" &&
      scope !== "org" &&
      Boolean(clientId) &&
      Boolean(matterId) &&
      workspaceQueryEnabled(scope, { clientId, matterId, caseId }),
    staleTime: 30_000,
    retry: 1,
  });
}
